import { useEffect, useReducer, useState } from 'react'
import BuildReadyStrip from './BuildReadyStrip.jsx'
import { wf } from '../outline/state.js'
import { catLabel } from '../outline/constants.js'
import { ctx } from '../outline/ctx.js'
import { blockedBy, msLabel } from '../outline/blocked.js'
import { StartedAheadTag, PostRecordTag } from './MilestoneFlowTags.jsx'
import { BlockedLock } from './ScheduleSection.jsx'
import CrewInline from './CrewInline.jsx'
import { getProjectScope, setProjectScope } from '../outline/projectScope.js'
import { flightsForMember, crewIndexReady } from '../outline/crew.js'
import { updatePhaseTimeEntries } from '../services/phases.js'
import { threadsForPhase, threadStatus } from '../services/threads.js'
import { deriveCandidateStage, candidateRfps, CLIENT_DECISION_LABELS } from '../services/consultantRfps.js'
import { submittalLabel } from '../services/submittals.js'
import { orderLabel, statusLabel } from './TeamLocalResources.jsx'
import { PRINT_STATUSES } from '../../../api/_printOrderOptions.js'
import {
  updateConceptUnit, deleteConceptUnitCascade, getConceptLevels,
  updateConceptLevel, deleteConceptLevelCascade, getLevelRooms, deleteRoom,
} from '../services/design.js'
import { confirmTypeDialog } from './Dialog.jsx'
import { UnitTypeModal, LevelRenameModal } from './ConceptUnitModals.jsx'
import { countNotConforming } from './ConceptDevMetricsSection.jsx'
import { CONCEPT_CATEGORIES } from './ConceptFieldCategorySection.jsx'
import SlackThreadPanel from './SlackThreadPanel.jsx'
import { openTeamInspirationBoard } from './inspiration/InspirationOutline.jsx'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'

function getFirst(val) {
  if (Array.isArray(val)) return val[0] || ''
  return val || ''
}

/** Resolve a consultant candidate's { id, label } from raw ctx data — mirrors outline.js's
 * breadcrumb resolver so the title bar and breadcrumb never disagree. */
function resolveCandidateLabel(projectConsultantId) {
  const pc = (ctx.teamProjectConsultants || []).find(p => p.id === projectConsultantId)
  if (!pc) return { id: projectConsultantId, label: 'Candidate' }
  const org = (ctx.teamOrganizations || []).find(o => o.id === pc.orgId)
  const label = (pc.consultantService || 'Consultant') + ' - ' + (org ? (org.fields['Organization Name'] || 'Unknown') : 'Unknown')
  return { id: projectConsultantId, label }
}

/* "Jun 19" — parses a date-only string in local time (no UTC shift). */
function fmtShortDate(d) {
  if (!d) return ''
  const s = String(d).slice(0, 10)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d)
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function ZoomTitle() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const [showPanel, setShowPanel] = useState(false)
  const [copied, setCopied] = useState(false)
  const [unitEditOpen, setUnitEditOpen] = useState(false)
  const [levelRenameOpen, setLevelRenameOpen] = useState(false)
  const sheetsMenu = useActionMenu()
  const inspoMenu = useActionMenu()
  const specMenu = useActionMenu()
  const meetingMenu = useActionMenu()
  const threadMenu = useActionMenu()
  const threadsRootMenu = useActionMenu()
  const unitMenu = useActionMenu()
  const levelMenu = useActionMenu()
  const roomMenu = useActionMenu()
  const unitsCatMenu = useActionMenu()
  const levelsCatMenu = useActionMenu()
  const roomsCatMenu = useActionMenu()
  const candidateMenu = useActionMenu()
  const printShopMenu = useActionMenu()
  const printOrderMenu = useActionMenu()
  const rfpMenu = useActionMenu()
  const proposalMenu = useActionMenu()

  function copyLink() {
    if (!ctx.copyShareLink) return
    Promise.resolve(ctx.copyShareLink()).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    })
  }

  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  // Close the Slack panel when the zoomed milestone changes.
  useEffect(() => { setShowPanel(false) }, [wf.zoomMsId])

  // Build the title string based on zoom state
  let title = ''
  // Optional trailing data shown at the end of the title line (no boxes) — e.g. counts.
  let titleMeta = ''

  if (wf.zoomItemId) {
    const items = ctx.clCache ? (ctx.clCache[wf.zoomMsId] || []) : []
    for (const item of items) {
      if (item.id === wf.zoomItemId) {
        title = getFirst(item.fields['Name for List (from Deliverable Checklist Item Template)']) || 'Item'
        break
      }
    }
  } else if (wf.zoomCat) {
    title = catLabel(wf.zoomCat)
  } else if (wf.zoomSection) {
    if (wf.zoomSection.startsWith('concept-detail:')) {
      const cdId = wf.zoomSection.split(':')[1] || ''
      const rec = (ctx.conceptCache || []).find(c => c.id === cdId)
      title = rec ? (rec.fields['Parti Pris Title'] || 'Concept') : 'Concept'
    } else if (wf.zoomSection.startsWith('unit-detail:')) {
      const udId = wf.zoomSection.split(':')[1] || ''
      let rec = null
      for (const cid of Object.keys(ctx.unitCache || {})) {
        rec = (ctx.unitCache[cid] || []).find(u => u.id === udId)
        if (rec) break
      }
      title = rec ? (rec.fields['Unit Type'] || rec.fields['Name'] || 'Unit') : 'Unit'
    } else if (wf.zoomSection.startsWith('level-detail:')) {
      const ldId = wf.zoomSection.split(':')[1] || ''
      let rec = null
      for (const uid of Object.keys(ctx.levelCache || {})) {
        rec = (ctx.levelCache[uid] || []).find(l => l.id === ldId)
        if (rec) break
      }
      title = rec ? (rec.fields['Name'] || 'Level') : 'Level'
    } else if (wf.zoomSection.startsWith('room-detail:')) {
      const rParts = wf.zoomSection.split(':')
      const rmId = rParts[1] || ''
      const rmLevelId = rParts[2] || ''
      const rec = (ctx.roomCache?.[rmLevelId] || []).find(r => r.id === rmId)
      title = rec ? (rec.fields['Room'] || rec.fields['Room Number and Name'] || 'Room') : 'Room'
    } else if (wf.zoomSection.startsWith('sec:concept:') && wf.zoomSection.endsWith(':metrics')) {
      const dmConceptId = wf.zoomSection.split(':')[2] || ''
      title = 'Development Metrics'
      const dmConcept = (ctx.conceptCache || []).find(c => c.id === dmConceptId)
      const dmNotConforming = countNotConforming(dmConcept)
      if (dmNotConforming > 0) titleMeta = dmNotConforming + ' not conforming'
    } else if (wf.zoomSection.startsWith('sec:concept:') && CONCEPT_CATEGORIES[wf.zoomSection.split(':')[3] || '']) {
      const ccSuffix = wf.zoomSection.split(':')[3] || ''
      title = CONCEPT_CATEGORIES[ccSuffix].title
    } else if (wf.zoomSection.startsWith('sec:concept:')) {
      const cuConceptId = wf.zoomSection.split(':')[2] || ''
      title = 'Units'
      const cuUnits = ctx.unitCache?.[cuConceptId]
      if (Array.isArray(cuUnits)) titleMeta = cuUnits.length + ' unit' + (cuUnits.length !== 1 ? 's' : '')
    } else if (wf.zoomSection.startsWith('sec:unit:')) {
      const ulUnitId = wf.zoomSection.split(':')[2] || ''
      title = 'Levels'
      const ulLevels = ctx.levelCache?.[ulUnitId]
      if (Array.isArray(ulLevels)) titleMeta = ulLevels.length + ' level' + (ulLevels.length !== 1 ? 's' : '')
    } else if (wf.zoomSection.startsWith('sec:level:')) {
      const lrLevelId = wf.zoomSection.split(':')[2] || ''
      title = 'Rooms'
      const lrRooms = ctx.roomCache?.[lrLevelId]
      if (Array.isArray(lrRooms)) titleMeta = lrRooms.length + ' room' + (lrRooms.length !== 1 ? 's' : '')
    } else if (wf.zoomSection.startsWith('parcel-detail:')) {
      const parts = wf.zoomSection.split(':')
      const id = parts[1] || ''
      const det = ctx.parcelDetailCache && ctx.parcelDetailCache[id]
      if (parts[2] === 'agencies') {
        title = 'Agencies'
        const jurisId = det && det.jurisdiction && det.jurisdiction.id
        const agencies = jurisId ? ctx.agencyCache?.[jurisId] : null
        if (Array.isArray(agencies)) titleMeta = agencies.length + ' agenc' + (agencies.length !== 1 ? 'ies' : 'y')
      } else {
        const p = det && det.parcel
        title = p ? (p.fields['APN'] || p.fields['Name'] || 'Parcel') : 'Parcel'
        const zoneName = det && det.zone && (det.zone.fields['Name of Zone'] || det.zone.fields['Name'])
        if (zoneName) titleMeta = zoneName
      }
    } else if (wf.zoomSection.startsWith('permit-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      const p = (ctx.permitCache || []).find(x => x.id === id)
      title = p ? (p.type || p.name || 'Permit') : 'Permit'
      const n = (ctx.permitReviewCache || []).filter(r => r.permitId === id).length
      if (Array.isArray(ctx.permitReviewCache)) titleMeta = n + ' agency review' + (n !== 1 ? 's' : '')
    } else if (wf.zoomSection.startsWith('review-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      const r = (ctx.permitReviewCache || []).find(x => x.id === id)
      title = r ? (r.agency || r.name || 'Agency Review') : 'Agency Review'
      if (r && r.status) titleMeta = (r.hasBall ? '⚽ ' : '') + r.status
    } else if (wf.zoomSection.startsWith('campaign-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      const c = (ctx.bidCampaignCache || []).find(x => x.id === id)
      title = c ? ('Bidding Campaign No. ' + (c.round_number ?? '—')) : 'Bid Campaign'
      const det = ctx.bidCampaignDetailCache?.[id]
      if (det && Array.isArray(det.bids)) titleMeta = det.bids.length + ' bid' + (det.bids.length !== 1 ? 's' : '')
    } else if (wf.zoomSection.startsWith('bid-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      let bid = null
      const cache = ctx.bidCampaignDetailCache || {}
      for (const cid of Object.keys(cache)) {
        const d = cache[cid]
        if (d && Array.isArray(d.bids)) { const f = d.bids.find(b => b.id === id); if (f) { bid = f; break } }
      }
      if (!bid) { const z = ctx.caZoomCache && ctx.caZoomCache[id]; if (z && typeof z === 'object' && z.id === id) bid = z }
      title = bid ? (bid.bidder_name || 'Bid') : 'Bid'
      if (bid && bid.price_normalized != null) titleMeta = '$' + Number(bid.price_normalized).toLocaleString('en-US', { maximumFractionDigits: 0 })
    } else if (wf.zoomSection.startsWith('bim-system-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      const s = (ctx.bimCache || []).find(x => x.id === id)
      title = s ? (s.name || s.fields?.['Name'] || 'BIM System') : 'BIM System'
      const els = ctx.loader?.get('bimElements:' + id)
      if (Array.isArray(els)) titleMeta = els.length + ' element' + (els.length !== 1 ? 's' : '')
      else if (s && s.stats) titleMeta = s.stats.elements + ' element' + (s.stats.elements !== 1 ? 's' : '')
    } else if (wf.zoomSection.startsWith('bim-element-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      let el = null
      const sysId = ctx.bimElementSystem?.[id]
      if (sysId) {
        const els = ctx.loader?.get('bimElements:' + sysId)
        if (Array.isArray(els)) el = els.find(e => e.id === id) || null
      }
      if (!el) el = ctx.bimZoomCache?.[id]?.element || null
      title = el ? (el.name || 'BIM Element') : 'BIM Element'
      if (el) titleMeta = el.status || ''
    } else if (wf.zoomSection.startsWith('bim-phase-detail:')) {
      const parts = wf.zoomSection.split(':')
      const id = parts[1] || ''
      const phase = parts[2] || ''
      let el = null
      const sysId = ctx.bimElementSystem?.[id]
      if (sysId) {
        const els = ctx.loader?.get('bimElements:' + sysId)
        if (Array.isArray(els)) el = els.find(e => e.id === id) || null
      }
      if (!el) el = ctx.bimZoomCache?.[id]?.element || null
      const phaseLabels = { sd: 'SD', dd: 'DD', cd: 'CD', ia: 'IA' }
      title = (phaseLabels[phase] || phase.toUpperCase()) + ' Criteria'
      if (el) {
        const list = el.criteria?.[phase] || []
        const done = list.filter(c => c.done).length
        titleMeta = done + '/' + list.length
      }
    } else if (wf.zoomSection.startsWith('ve-effort:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      const e = (ctx.veEffortCache || []).find(x => x.id === id)
      title = e ? ('VE Effort No. ' + (e.effort_number ?? '—')) : 'VE Effort'
      if (e && Array.isArray(e.ve_items)) {
        const acc = e.ve_items.filter(i => i.disposition === 'accepted').reduce((s, i) => s + Number(i.estimated_savings || 0), 0)
        if (acc > 0) titleMeta = '$' + acc.toLocaleString('en-US', { maximumFractionDigits: 0 }) + ' accepted'
        else titleMeta = e.ve_items.length + ' item' + (e.ve_items.length !== 1 ? 's' : '')
      }
    } else if (wf.zoomSection.startsWith('ve-item:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      let item = null
      for (const e of (ctx.veEffortCache || [])) { const f = (e.ve_items || []).find(i => i.id === id); if (f) { item = f; break } }
      title = item ? (item.title || 'VE Item') : 'VE Item'
      if (item && item.estimated_savings != null) titleMeta = '$' + Number(item.estimated_savings).toLocaleString('en-US', { maximumFractionDigits: 0 })
    } else if (wf.zoomSection.startsWith('contract-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      const c = (ctx.contractCache || []).find(x => x.id === id)
      title = c ? (c.contractor_name || 'Construction Contract') : 'Construction Contract'
      if (c && c.contract_value != null) titleMeta = '$' + Number(c.contract_value).toLocaleString('en-US', { maximumFractionDigits: 0 })
    } else if (wf.zoomSection.startsWith('rfi-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      let rfi = null
      const cache = ctx.rfiCache || {}
      for (const cid of Object.keys(cache)) {
        const list = cache[cid]
        if (Array.isArray(list)) { const f = list.find(r => r.id === id); if (f) { rfi = f; break } }
      }
      if (!rfi) { // RFI reached via Schedule > Threads lives in the thread-mount cache
        const tc = ctx.threadCaItemCache || {}
        for (const tid of Object.keys(tc)) { const e = tc[tid]; if (e && e.kind === 'rfi' && e.item && e.item.id === id) { rfi = e.item; break } }
      }
      if (!rfi) { const z = ctx.caZoomCache && ctx.caZoomCache[id]; if (z && typeof z === 'object' && z.id === id) rfi = z }
      title = rfi ? ('RFI ' + (rfi.rfi_number ?? '') + (rfi.subject ? ' — ' + rfi.subject : '')) : 'RFI'
      if (rfi && rfi.status) titleMeta = rfi.status.charAt(0).toUpperCase() + rfi.status.slice(1)
    } else if (wf.zoomSection.startsWith('submittal-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      let s = null
      const cache = ctx.submittalCache || {}
      for (const cid of Object.keys(cache)) {
        const list = cache[cid]
        if (Array.isArray(list)) { const f = list.find(x => x.id === id); if (f) { s = f; break } }
      }
      if (!s) { // submittal reached via Schedule > Threads lives in the thread-mount cache
        const tc = ctx.threadCaItemCache || {}
        for (const tid of Object.keys(tc)) { const e = tc[tid]; if (e && e.kind === 'submittal' && e.item && e.item.id === id) { s = e.item; break } }
      }
      if (!s) { const z = ctx.caZoomCache && ctx.caZoomCache[id]; if (z && typeof z === 'object' && z.id === id) s = z }
      title = s ? submittalLabel(s) : 'Submittal'
      if (s && s.status) titleMeta = s.status.charAt(0).toUpperCase() + s.status.slice(1)
    } else if (wf.zoomSection.startsWith('asi-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      let a = null
      const cache = ctx.asiCache || {}
      for (const cid of Object.keys(cache)) {
        const list = cache[cid]
        if (Array.isArray(list)) { const f = list.find(x => x.id === id); if (f) { a = f; break } }
      }
      if (!a) { const z = ctx.caZoomCache && ctx.caZoomCache[id]; if (z && typeof z === 'object' && z.id === id) a = z }
      title = a ? ('ASI ' + (a.asi_number ?? '') + (a.subject ? ' — ' + a.subject : '')) : 'ASI'
      if (a && a.status) titleMeta = a.status.charAt(0).toUpperCase() + a.status.slice(1)
    } else if (wf.zoomSection.startsWith('proposal-request-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      let pr = null
      const cache = ctx.proposalRequestCache || {}
      for (const cid of Object.keys(cache)) { const l = cache[cid]; if (Array.isArray(l)) { const f = l.find(p => p.id === id); if (f) { pr = f; break } } }
      if (!pr) { const z = ctx.caZoomCache && ctx.caZoomCache[id]; if (z && typeof z === 'object' && z.id === id) pr = z }
      title = pr ? ((pr.pr_number ? pr.pr_number + ' — ' : '') + (pr.subject || 'Proposal Request')) : 'Proposal Request'
      if (pr && pr.status) {
        const lab = { open: 'Open', proposal_received: 'Proposal received', closed: 'Closed', void: 'Void' }
        titleMeta = lab[pr.status] || pr.status
      }
    } else if (wf.zoomSection.startsWith('spec-section-detail:')) {
      const id = wf.zoomSection.split(':')[1] || ''
      let s = (ctx.specSectionCache || []).find(x => x.id === id)
      if (!s) { const z = ctx.caZoomCache && ctx.caZoomCache[id]; if (z && typeof z === 'object' && z.id === id) s = z }
      title = s ? ([s.section_number, s.section_name].filter(Boolean).join(' — ')) : 'Spec Section'
      if (s && s.status) titleMeta = s.status.charAt(0).toUpperCase() + s.status.slice(1)
    } else if (wf.zoomSection.startsWith('sec:contract:')) {
      const rest = wf.zoomSection.slice('sec:contract:'.length)
      const lc = rest.lastIndexOf(':')
      const ci = rest.slice(0, lc)
      const type = rest.slice(lc + 1)
      title = type === 'rfis' ? 'RFIs' : type === 'submittals' ? 'Submittals' : type === 'asis' ? 'ASIs' : type === 'prs' ? 'Proposal Requests' : type
      const cache = type === 'rfis' ? ctx.rfiCache : type === 'submittals' ? ctx.submittalCache : type === 'asis' ? ctx.asiCache : type === 'prs' ? ctx.proposalRequestCache : null
      const list = cache && cache[ci]
      if (Array.isArray(list)) {
        const noun = type === 'rfis' ? 'RFI' : type === 'submittals' ? 'submittal' : type === 'asis' ? 'ASI' : 'proposal request'
        titleMeta = list.length + ' ' + noun + (list.length !== 1 ? 's' : '')
      }
    } else if (wf.zoomSection.startsWith('sec:construction:')) {
      const t = wf.zoomSection.slice('sec:construction:'.length)
      title = t === 'bid' ? 'Bid Campaigns' : t === 've' ? 'Value Engineering' : t === 'contracts' ? 'Construction Contracts' : t
      if (t === 'bid' && Array.isArray(ctx.bidCampaignCache)) titleMeta = ctx.bidCampaignCache.length + ' campaign' + (ctx.bidCampaignCache.length !== 1 ? 's' : '')
    } else if (wf.zoomSection.startsWith('sec:threads:cat:')) {
      // Thread category zoom under Reference > Threads
      title = wf.zoomSection.slice('sec:threads:cat:'.length)
      if (Array.isArray(ctx.threadCache)) {
        const n = ctx.threadCache.filter(t => (t.fields['Type'] || 'Misc. Items') === title).length
        titleMeta = n + ' thread' + (n !== 1 ? 's' : '')
      }
    } else if (wf.zoomSection.startsWith('thread-detail:')) {
      // Individual thread zoom — title is the thread subject, meta is its status
      const id = wf.zoomSection.slice('thread-detail:'.length)
      const t = (ctx.threadCache || []).find(x => x.id === id)
      title = t ? (t.fields['Title'] || 'Thread') : 'Thread'
      const st = t && t.fields['Status'] ? String(t.fields['Status']).replace(/^\S+\s/, '').trim() : ''
      if (st) titleMeta = st
    } else if (wf.zoomSection.startsWith('meeting-detail:')) {
      // Meeting minutes detail — title is "<Type> — <Date>", meta is the workflow status/stage
      const id = wf.zoomSection.slice('meeting-detail:'.length)
      const m = (ctx.meetingCache || []).find(x => x.id === id)
      if (m) {
        const mtype = m.fields['Type'] || 'Meeting'
        const ds = m.fields['Meeting Date String'] || ''
        title = mtype + (ds ? ' — ' + ds : '')
        // Title-line meta mirrors the outline row's right side: status + item count. Status once
        // posted also signals the minutes are locked.
        const mStatus = m.fields['Posted'] === true ? 'Posted · Locked' : (m.fields['Status Message'] || m.fields['Status'] || '')
        const mItems = (m.fields['Threads'] || []).length
        titleMeta = [mStatus, mItems ? mItems + ' item' + (mItems !== 1 ? 's' : '') : ''].filter(Boolean).join('  ·  ')
      } else { title = 'Meeting' }
    } else if (wf.zoomSection.startsWith('refdoc-grp:')) {
      // Reference Documents group — title is the kit item (or "Other files"), meta the file count
      const gid = wf.zoomSection.slice('refdoc-grp:'.length)
      const c = ctx.refDocCache && ctx.refDocCache[wf.zoomProjectId]
      const ds = ((c && c.documents) || []).filter(d => (d.fact_id || 'none') === gid)
      title = gid === 'none' ? 'Other files' : (ds[0] && ds[0].fact_name) || 'Kit item'
      if (c) titleMeta = ds.length + ' file' + (ds.length !== 1 ? 's' : '')
    } else if (wf.zoomSection.startsWith('meeting-cat:')) {
      // Meeting category focus — title is the meeting type, meta is the count
      const t = wf.zoomSection.slice('meeting-cat:'.length)
      title = t
      if (Array.isArray(ctx.meetingCache)) {
        const n = ctx.meetingCache.filter(m => ((m.fields && m.fields['Type']) || 'Other') === t).length
        titleMeta = n + ' meeting' + (n !== 1 ? 's' : '')
      }
    } else if (wf.zoomSection.startsWith('sec:meeting:')) {
      // Meeting sub-section: sec:meeting:<id>:details | :threads
      const sub = wf.zoomSection.split(':')[3] || ''
      title = sub === 'details' ? 'Meeting Details' : 'Threads'
    } else if (wf.zoomSection.startsWith('sec:team:group:')) {
      // Team group focus — title is the group name, meta is its member count
      const gkey = wf.zoomSection.slice('sec:team:group:'.length)
      title = gkey === 'alder-james' ? 'Alder James' : gkey === 'client' ? 'Client' : gkey === 'general-contractor' ? 'General Contractor' : gkey === 'local-resources' ? 'Local Resources' : 'Subconsultants'
      const g = ctx.teamGrouped
      if (gkey === 'local-resources') {
        const n = (ctx.teamOrganizations || []).filter(o => [].concat(o.fields['Type'] || []).includes('Reprographics')).length
        if (ctx.teamOrganizations) titleMeta = n + ' print shop' + (n === 1 ? '' : 's')
      } else if (g) {
        const n = gkey === 'alder-james' ? g.alderJames.length
          : gkey === 'client' ? g.client.length
          : gkey === 'general-contractor' ? (g.generalContractor ? g.generalContractor.members.length : 0)
          : g.subconsultants.reduce((s, x) => s + x.members.length, 0)
        titleMeta = n + ' ' + (gkey === 'subconsultants' ? 'people' : 'member' + (n !== 1 ? 's' : ''))
      }
    } else if (wf.zoomSection.startsWith('sec:team:org:')) {
      // Subconsultant org focus — title is the org's discipline label, meta is its member count
      const orgId = wf.zoomSection.slice('sec:team:org:'.length)
      const g = ctx.teamGrouped
      const sub = g ? g.subconsultants.find(x => x.org.id === orgId) : null
      title = sub ? sub.label : 'Organization'
      if (sub) titleMeta = sub.members.length + ' ' + (sub.members.length !== 1 ? 'people' : 'person')
    } else if (wf.zoomSection.startsWith('sec:team:shop:')) {
      // Print shop focus: title is the shop, meta is its order count on this project
      const orgId = wf.zoomSection.slice('sec:team:shop:'.length)
      const org = (ctx.teamOrganizations || []).find(o => o.id === orgId)
      title = org ? (org.fields['Organization Name'] || 'Print shop') : 'Print shop'
      if (ctx.printOrderCache) {
        const n = ctx.printOrderCache.filter(o => o.org_airtable_id === orgId).length
        titleMeta = n + ' print order' + (n === 1 ? '' : 's')
      }
    } else if (wf.zoomSection.startsWith('sec:team:printorder:')) {
      // Print order focus: title is its date / files / approval, meta its status
      const id = wf.zoomSection.slice('sec:team:printorder:'.length)
      const order = (ctx.printOrderCache || []).find(o => o.id === id)
      title = order ? 'Print order ' + orderLabel(order) : 'Print order'
      if (order) titleMeta = statusLabel(order)
    } else if (wf.zoomSection.startsWith('candidate-detail:')) {
      // Candidate focus — title is its label, meta is the derived lifecycle stage
      const pcId = wf.zoomSection.slice('candidate-detail:'.length)
      const cand = resolveCandidateLabel(pcId)
      title = cand.label
      const rfpData = ctx.consultantRfpCache
      if (rfpData) {
        const stage = deriveCandidateStage(pcId, rfpData.rfps, rfpData.proposals)
        titleMeta = stage.icon + ' ' + stage.label
      }
    } else if (wf.zoomSection.startsWith('rfp-detail:')) {
      // RFP round focus — title is the sent date, meta is the recipient count
      const rfpId = wf.zoomSection.slice('rfp-detail:'.length)
      const rfpData = ctx.consultantRfpCache
      const rfp = rfpData ? (rfpData.rfps || []).find(r => r.id === rfpId) : null
      title = 'RFP sent ' + (rfp && rfp.sent_at ? String(rfp.sent_at).slice(0, 10) : '')
      if (rfp) {
        const n = (rfp.recipients || []).length
        titleMeta = n + ' recipient' + (n !== 1 ? 's' : '')
      }
    } else if (wf.zoomSection.startsWith('proposal-detail:')) {
      // Proposal focus — title is the fee, meta is the client-decision badge
      const proposalId = wf.zoomSection.slice('proposal-detail:'.length)
      const rfpData = ctx.consultantRfpCache
      const proposal = rfpData ? (rfpData.proposals || []).find(p => p.id === proposalId) : null
      title = proposal && proposal.proposed_fee != null
        ? 'Proposal – $' + Number(proposal.proposed_fee).toLocaleString('en-US', { maximumFractionDigits: 0 })
        : 'Proposal'
      if (proposal && proposal.client_decision) titleMeta = CLIENT_DECISION_LABELS[proposal.client_decision] || proposal.client_decision
    } else if (wf.zoomSection.startsWith('sec:phase:')) {
      // Phase sub-section zoom: sec:phase:{phId}:{milestones|threads|thcat:Type}
      const psp = wf.zoomSection.split(':')
      const psPhId = psp[2] || ''
      const psType = psp[3] || ''
      const psPh = (ctx.state?.phases || []).find(p => p.id === psPhId)
      if (psType === 'thcat') {
        const catType = wf.zoomSection.slice(wf.zoomSection.lastIndexOf(':') + 1)
        title = catType
        if (psPh && Array.isArray(ctx.threadCache)) {
          const n = threadsForPhase(ctx.threadCache, psPh).filter(t => (t.fields['Type'] || 'Misc. Items') === catType).length
          titleMeta = n + ' thread' + (n !== 1 ? 's' : '')
        }
      } else if (psType === 'threads') {
        title = 'Threads'
        if (psPh && Array.isArray(ctx.threadCache)) {
          const n = threadsForPhase(ctx.threadCache, psPh).length
          titleMeta = n + ' thread' + (n !== 1 ? 's' : '')
        }
      } else {
        title = 'Milestones'
        const pname = psPh?.fields?.['Phase Name'] || ''
        if (pname) {
          const n = (ctx.state?.milestones || []).filter(m => (m.fields['Project Phase'] || '') === pname).length
          titleMeta = n + ' milestone' + (n !== 1 ? 's' : '')
        }
      }
    } else {
      // Sheet zoom: sec:prod:sheet:{sheetId} or sec:{msId}:sheet:{sheetId}(:cl)
      const sheetMatch = wf.zoomSection.match(/^sec:[^:]+:sheet:([^:]+)(?::(.+))?$/)
      // Discipline zoom: sec:prod:disc:Architectural or sec:{msId}:disc:Interior Architecture
      const discMatch = !sheetMatch && wf.zoomSection.match(/^sec:[^:]+:disc:(.+)$/)
      if (sheetMatch) {
        const sheetId  = sheetMatch[1]
        const subKey   = sheetMatch[2]  // 'cl' or undefined
        let sheetLabel = sheetId
        // Production context: projSheetCache has Drawing Sheet records
        if (Array.isArray(ctx.projSheetCache)) {
          const found = ctx.projSheetCache.find(s => s.id === sheetId)
          if (found) sheetLabel = getFirst(found.fields['Sheet Number and Title']) || sheetId
        }
        // Milestone context: sheetCache[msId] has revision records
        if (sheetLabel === sheetId) {
          for (const msId of Object.keys(ctx.sheetCache || {})) {
            const rev = (ctx.sheetCache[msId] || []).find(r => (r.fields['Drawing Sheet'] || [])[0] === sheetId)
            if (rev) {
              sheetLabel = getFirst(rev.fields['Sheet Number and Title (from Drawing Sheet)']) || sheetId
              break
            }
          }
        }
        title = subKey === 'cl' ? sheetLabel + ' · Checklist' : sheetLabel
      } else if (discMatch) {
        title = discMatch[1]
      } else {
        const lastColon = wf.zoomSection.lastIndexOf(':')
        const stype = lastColon !== -1 ? wf.zoomSection.slice(lastColon + 1) : ''
        const MAP = {
          checklist: 'Checklist', bim: 'BIM Systems',
          sheets: 'Drawing Sheets', concepts: 'Concepts', 'design-concept': 'Design',
          criteria: 'Criteria', parcels: 'Parcels', specs: 'Specifications',
          documents: 'Documents', gate: 'Gate', feeder: 'Feeder', 'spec-versions': 'Specifications',
          'threads-riding': 'Threads', inspiration: 'Client Inspiration',
        }
        title = MAP[stype] || stype
      }
    }
  } else if (wf.zoomMsId) {
    const ms = (ctx.state?.milestones || []).find(m => m.id === wf.zoomMsId)
    title = ms ? (ms.fields['Full Deliverable Name'] || ms.fields['Name'] || 'Milestone') : 'Milestone'
  } else if (wf.zoomPhaseId) {
    const ph = (ctx.state?.phases || []).find(p => p.id === wf.zoomPhaseId)
    title = ph ? (ph.fields['Phase Name'] || ph.fields['Name'] || 'Phase') : 'Phase'
  } else if (wf.zoomConceptId) {
    const rec = (ctx.conceptCache || []).find(c => c.id === wf.zoomConceptId)
    title = rec ? (rec.fields['Parti Pris Title'] || 'Unnamed Concept') : 'Unnamed Concept'
  } else if (wf.zoomRoot) {
    title = wf.zoomRoot.charAt(0).toUpperCase() + wf.zoomRoot.slice(1)
    if (wf.zoomRoot === 'permits' && Array.isArray(ctx.permitCache)) {
      titleMeta = ctx.permitCache.length + ' permit' + (ctx.permitCache.length !== 1 ? 's' : '')
    } else if (wf.zoomRoot === 'documents' && ctx.refDocCache && ctx.refDocCache[wf.zoomProjectId]) {
      const n = (ctx.refDocCache[wf.zoomProjectId].documents || []).length
      titleMeta = n + ' file' + (n !== 1 ? 's' : '')
    } else if (wf.zoomRoot === 'threads' && Array.isArray(ctx.threadCache)) {
      titleMeta = ctx.threadCache.length + ' thread' + (ctx.threadCache.length !== 1 ? 's' : '')
    }
  } else if (wf.zoomProjectId) {
    // Zoomed into a project but no root section selected yet
    const proj = (ctx.projectsCache || []).find(p => p.id === wf.zoomProjectId)
    title = proj ? (proj.fields['Project Name'] || proj.fields['Project Number'] || 'Project') : 'Project'
  } else if (ctx.showClosedProjects) {
    // Home, toggled to the closed-projects view (Close Out Project — docs/PROJECT_CLOSEOUT.md)
    title = 'Closed Projects'
    if (Array.isArray(ctx.closedProjectsCache)) {
      titleMeta = ctx.closedProjectsCache.length + ' project' + (ctx.closedProjectsCache.length !== 1 ? 's' : '')
    }
  } else {
    // Home — the project list reads as a zoom into "Projects"
    title = 'Projects'
    if (Array.isArray(ctx.projectsCache)) {
      // Mirror ProjectListSection's own filters (on-hold, then project scope) so the count
      // matches what's actually shown.
      let shown = (!ctx.showOnHoldProjects && ctx.projectHoldIds)
        ? ctx.projectsCache.filter(p => !ctx.projectHoldIds.has(p.id))
        : ctx.projectsCache
      if (getProjectScope() === 'mine' && wf.currentMemberId && crewIndexReady()) {
        const mine = new Set(flightsForMember(wf.currentMemberId).map(f => f.projectId).filter(Boolean))
        shown = shown.filter(p => mine.has(p.id))
      }
      titleMeta = shown.length + ' project' + (shown.length !== 1 ? 's' : '')
    }
  }

  if (!title) return null

  // Build menu button data attrs
  let menuAttrs = null
  let sheetsZoom = null   // set for a Drawing Sheets section zoom → render a React menu instead
  let inspoZoom = false   // Client Inspiration section zoom → React menu (Open inspiration board)
  let specZoom = null     // set for a Specifications section zoom → React menu (Open spec editor)
  let meetingZoom = null  // set for a meeting-detail zoom → React menu (meeting actions via events)
  const isMeetingDetail = !!(wf.zoomSection && wf.zoomSection.startsWith('meeting-detail:'))
  if (isMeetingDetail) {
    const _mid = wf.zoomSection.slice('meeting-detail:'.length)
    const _m = (ctx.meetingCache || []).find(x => x.id === _mid)
    const _draft = (ctx.meetingNotesDraftCache || {})[_mid] || null
    meetingZoom = {
      id: _mid,
      posted: !!(_m && _m.fields['Posted'] === true),
      notesImported: !!(_m && _m.fields['Notes Imported'] === true),
      draftStatus: _draft ? _draft.status : null,   // 'draft' | 'ready_for_review' | null
      pdfUrl: (_m && _m.fields['Posted Meeting Minutes'] && _m.fields['Posted Meeting Minutes'][0] && _m.fields['Posted Meeting Minutes'][0].url) || '',
    }
  }

  // Thread-detail zoom → its status/team/Slack + ⋯ menu render inline on the title (the node renders
  // children-only), so the meta no longer sits on its own line below the title.
  let threadZoom = null
  const isThreadDetail = !!(wf.zoomSection && wf.zoomSection.startsWith('thread-detail:'))
  if (isThreadDetail) {
    const _tid = wf.zoomSection.slice('thread-detail:'.length)
    const _t = (ctx.threadCache || []).find(x => x.id === _tid)
    const _tf = _t ? _t.fields : {}
    const _status = threadStatus(_tf)   // Open / Closed / STUCK only — no Stale, no OVERDUE (2026-09-10)
    threadZoom = {
      id: _tid,
      title: _tf['Title'] || 'Thread',
      status: _status ? String(_status).replace(/^\S+\s/, '').trim() : '',
      // No thread date of its own (2026-09-10): the plane it rides lends its target finish.
      deadline: _status.includes('Closed') ? '' : ((((ctx.state && ctx.state.milestones) || []).find(m => m.id === (_tf['Milestone'] || [])[0]) || { fields: {} }).fields['Latest Finish (LFT)'] || ''),
      projectId: (_tf['Project'] || [])[0] || (wf.zoomProjectId || ''),
      projectName: (_tf['Project Name (from Project)'] || [])[0] || '',
      threadTs: _tf['Slack Thread Time Stamp'] || '',
    }
  }

  // Threads root zoom → React ⋯ menu (Add thread / expand / collapse) instead of the engine root menu
  // and the old in-content toolbar. The count shows as titleMeta on the title line.
  const isThreadsRootZoom = wf.zoomRoot === 'threads' && !wf.zoomSection && !wf.zoomPhaseId &&
    !wf.zoomMsId && !wf.zoomCat && !wf.zoomItemId

  // Concept/Unit/Level/Room detail zooms + the Units/Levels/Rooms category-wrapper zooms —
  // Unit/Level/Room get dedicated React ⋯ menus (Edit/Delete) below; Concept instead reuses the
  // native engine menu (data-type "concept" in outline.js) via menuAttrs further down, matching
  // the one ConceptRow's own ⋯ button already opens in the unzoomed tree view.
  let unitDetailZoom = null
  const isUnitDetail = !!(wf.zoomSection && wf.zoomSection.startsWith('unit-detail:'))
  if (isUnitDetail) {
    const _udParts = wf.zoomSection.split(':')
    const _udId = _udParts[1] || ''
    const _udConceptId = _udParts[2] || ''
    let _udRec = null
    for (const cid of Object.keys(ctx.unitCache || {})) {
      _udRec = (ctx.unitCache[cid] || []).find(u => u.id === _udId)
      if (_udRec) break
    }
    unitDetailZoom = { id: _udId, conceptId: _udConceptId, unitType: _udRec ? _udRec.fields['Unit Type'] : '' }
  }

  let levelDetailZoom = null
  const isLevelDetail = !!(wf.zoomSection && wf.zoomSection.startsWith('level-detail:'))
  if (isLevelDetail) {
    const _ldParts = wf.zoomSection.split(':')
    const _ldId = _ldParts[1] || ''
    const _ldUnitId = _ldParts[2] || ''
    let _ldRec = null
    for (const uid of Object.keys(ctx.levelCache || {})) {
      _ldRec = (ctx.levelCache[uid] || []).find(l => l.id === _ldId)
      if (_ldRec) break
    }
    levelDetailZoom = { id: _ldId, unitId: _ldUnitId, name: _ldRec ? (_ldRec.fields['Name'] || 'Level') : 'Level' }
  }

  const isConceptDetail    = !!(wf.zoomSection && wf.zoomSection.startsWith('concept-detail:'))
  const isRoomDetail       = !!(wf.zoomSection && wf.zoomSection.startsWith('room-detail:'))
  // Room's own settings (Name/Template/Scope/Number) are all editable inline in the content area
  // (RoomFieldsList, shared with the tree view) — this menu only needs to offer Delete.
  let roomDetailZoom = null
  if (isRoomDetail) {
    const _rdParts = wf.zoomSection.split(':')
    const _rdId = _rdParts[1] || ''
    const _rdLevelId = _rdParts[2] || ''
    const _rdRec = (ctx.roomCache?.[_rdLevelId] || []).find(r => r.id === _rdId)
    roomDetailZoom = { id: _rdId, levelId: _rdLevelId, name: _rdRec ? (_rdRec.fields['Room'] || _rdRec.fields['Room Number and Name'] || 'Room') : 'Room' }
  }
  const conceptCategorySuffix = wf.zoomSection && wf.zoomSection.startsWith('sec:concept:') ? (wf.zoomSection.split(':')[3] || '') : ''
  const conceptUnitsZoom   = (wf.zoomSection && wf.zoomSection.startsWith('sec:concept:') && !wf.zoomSection.endsWith(':metrics') && !CONCEPT_CATEGORIES[conceptCategorySuffix]) ? { conceptId: wf.zoomSection.split(':')[2] || '' } : null
  const conceptMetricsZoom = (wf.zoomSection && wf.zoomSection.startsWith('sec:concept:') && wf.zoomSection.endsWith(':metrics')) ? { conceptId: wf.zoomSection.split(':')[2] || '' } : null
  const conceptCategoryZoom = (wf.zoomSection && wf.zoomSection.startsWith('sec:concept:') && CONCEPT_CATEGORIES[conceptCategorySuffix]) ? { conceptId: wf.zoomSection.split(':')[2] || '' } : null
  const unitLevelsZoom     = (wf.zoomSection && wf.zoomSection.startsWith('sec:unit:')) ? { unitId: wf.zoomSection.split(':')[2] || '' } : null
  const levelRoomsZoom     = (wf.zoomSection && wf.zoomSection.startsWith('sec:level:')) ? { levelId: wf.zoomSection.split(':')[2] || '' } : null
  const isDesignEntityZoom = isConceptDetail || isUnitDetail || isLevelDetail || isRoomDetail ||
    !!conceptUnitsZoom || !!conceptMetricsZoom || !!conceptCategoryZoom || !!unitLevelsZoom || !!levelRoomsZoom

  const printShopZoom  = (wf.zoomSection && wf.zoomSection.startsWith('sec:team:shop:')) ? { orgId: wf.zoomSection.slice('sec:team:shop:'.length) } : null
  const printOrderZoom = (wf.zoomSection && wf.zoomSection.startsWith('sec:team:printorder:')) ? { id: wf.zoomSection.slice('sec:team:printorder:'.length) } : null
  const printOrderRec  = printOrderZoom ? (ctx.printOrderCache || []).find(o => o.id === printOrderZoom.id) : null
  const candidateDetailZoom = (wf.zoomSection && wf.zoomSection.startsWith('candidate-detail:')) ? { id: wf.zoomSection.slice('candidate-detail:'.length) } : null
  const rfpDetailZoom       = (wf.zoomSection && wf.zoomSection.startsWith('rfp-detail:')) ? { id: wf.zoomSection.slice('rfp-detail:'.length) } : null
  const proposalDetailZoom  = (wf.zoomSection && wf.zoomSection.startsWith('proposal-detail:')) ? { id: wf.zoomSection.slice('proposal-detail:'.length) } : null

  async function handleDeleteUnitZoom() {
    unitMenu.close()
    if (!unitDetailZoom) return
    const levels = await getConceptLevels(unitDetailZoom.id)
    const roomCounts = await Promise.all(levels.map(l => getLevelRooms(l.id)))
    const roomCount = roomCounts.reduce((sum, r) => sum + r.length, 0)
    const unitName = unitDetailZoom.unitType || 'Unit'
    const ok = await confirmTypeDialog({
      title: 'Delete unit?',
      message: `This will permanently delete "${unitName}", ${levels.length} level(s), and ${roomCount} room(s) under it. This cannot be undone.`,
    })
    if (!ok) return
    await deleteConceptUnitCascade(unitDetailZoom.id)
    if (ctx.unitCache?.[unitDetailZoom.conceptId]) {
      ctx.unitCache[unitDetailZoom.conceptId] = ctx.unitCache[unitDetailZoom.conceptId].filter(u => u.id !== unitDetailZoom.id)
    }
    if (ctx.levelCache) delete ctx.levelCache[unitDetailZoom.id]
    wf.zoomSection = 'sec:concept:' + unitDetailZoom.conceptId + ':units'
    ctx.render()
  }

  async function handleRenameUnitZoom(unitType) {
    if (!unitDetailZoom) return
    await updateConceptUnit(unitDetailZoom.id, { 'Unit Type': unitType })
    if (ctx.unitCache?.[unitDetailZoom.conceptId]) {
      ctx.unitCache[unitDetailZoom.conceptId] = ctx.unitCache[unitDetailZoom.conceptId].map(u =>
        u.id === unitDetailZoom.id ? { ...u, fields: { ...u.fields, 'Unit Type': unitType } } : u)
    }
    ctx.render()
  }

  async function handleDeleteLevelZoom() {
    levelMenu.close()
    if (!levelDetailZoom) return
    const rooms = await getLevelRooms(levelDetailZoom.id)
    const ok = await confirmTypeDialog({
      title: 'Delete level?',
      message: `This will permanently delete "${levelDetailZoom.name}" and ${rooms.length} room(s) under it. This cannot be undone.`,
    })
    if (!ok) return
    await deleteConceptLevelCascade(levelDetailZoom.id)
    if (ctx.levelCache?.[levelDetailZoom.unitId]) {
      ctx.levelCache[levelDetailZoom.unitId] = ctx.levelCache[levelDetailZoom.unitId].filter(l => l.id !== levelDetailZoom.id)
    }
    wf.zoomSection = 'sec:unit:' + levelDetailZoom.unitId + ':levels'
    ctx.render()
  }

  async function handleRenameLevelZoom(name) {
    if (!levelDetailZoom) return
    await updateConceptLevel(levelDetailZoom.id, { Name: name })
    if (ctx.levelCache?.[levelDetailZoom.unitId]) {
      ctx.levelCache[levelDetailZoom.unitId] = ctx.levelCache[levelDetailZoom.unitId].map(l =>
        l.id === levelDetailZoom.id ? { ...l, fields: { ...l.fields, Name: name } } : l)
    }
    ctx.render()
  }

  async function handleDeleteRoomZoom() {
    roomMenu.close()
    if (!roomDetailZoom) return
    const ok = await confirmTypeDialog({
      title: 'Delete room?',
      message: `This will permanently delete "${roomDetailZoom.name}". This cannot be undone.`,
    })
    if (!ok) return
    await deleteRoom(roomDetailZoom.id)
    if (ctx.roomCache?.[roomDetailZoom.levelId]) {
      ctx.roomCache[roomDetailZoom.levelId] = ctx.roomCache[roomDetailZoom.levelId].filter(r => r.id !== roomDetailZoom.id)
    }
    wf.zoomSection = 'sec:level:' + roomDetailZoom.levelId + ':rooms'
    ctx.render()
  }

  if (wf.zoomPhaseId && !wf.zoomMsId) {
    const ph = (ctx.state?.phases || []).find(p => p.id === wf.zoomPhaseId)
    const allowTime = ph ? ph.fields['Allow Time Entries'] === true : false
    menuAttrs = {
      'data-fn': 'openMenu',
      'data-type': 'phase',
      'data-key': 'ph:' + wf.zoomPhaseId,
      'data-pid': wf.zoomPhaseId,
      'data-allowtime': String(allowTime),
    }
  } else if (wf.zoomMsId && !wf.zoomCat && !wf.zoomSection) {
    // Only the milestone itself (no section/cat) gets the milestone menu. A milestone-owned SECTION
    // zoom (sec:{msId}:sheets|checklist|coord|bim) falls through to the section branch below, so it
    // gets that section's own menu — not the milestone menu (bug 1).
    menuAttrs = {
      'data-fn': 'openMenu',
      'data-type': 'ms',
      'data-key': 'ms:' + wf.zoomMsId,
      'data-msid': wf.zoomMsId,
      'data-pid': wf.zoomPhaseId || '',
    }
  } else if (wf.zoomCat) {
    menuAttrs = {
      'data-fn': 'openMenu',
      'data-type': 'cat',
      'data-key': 'cat:' + wf.zoomMsId + ':' + wf.zoomCat,
      'data-msid': wf.zoomMsId,
      'data-cat': wf.zoomCat,
    }
  } else if (wf.zoomSection && !/^(permit-detail:|review-detail:|campaign-detail:|bid-detail:|ve-effort:|ve-item:|contract-detail:|rfi-detail:|submittal-detail:|asi-detail:|spec-section-detail:|proposal-request-detail:|bim-system-detail:|bim-element-detail:|bim-phase-detail:|parcel-detail:|thread-detail:|meeting-detail:|meeting-cat:|refdoc-grp:|sec:meeting:|sec:construction:|sec:contract:|concept-detail:|unit-detail:|level-detail:|room-detail:|sec:concept:|sec:unit:|sec:level:|sec:team:|candidate-detail:|rfp-detail:|proposal-detail:)/.test(wf.zoomSection)) {
    /* Section zoom: extract sectiontype from the key.
       (New permit/construction entity zooms are excluded — their content has its own
        node-level action menus; no native section menu on the title bar.)
       sec:prod:sheets      → 'sheets'
       sec:design:bim       → 'bim'
       sec:{msId}:checklist → 'checklist'  (msId extracted from owner segment)
       sec:prod:disc:Arch   → '' (disc groups have no special menu items)
       Must come BEFORE the zoomRoot check because zoomRoot is also set during section zoom. */
    const _isDisc = wf.zoomSection.includes(':disc:')
    const _lastColon = wf.zoomSection.lastIndexOf(':')
    const _stype = (!_isDisc && _lastColon !== -1) ? wf.zoomSection.slice(_lastColon + 1) : ''
    const _owner = (wf.zoomSection.split(':')[1] || '')
    const _secMsId = _owner.startsWith('rec') ? _owner : ''
    if (_stype === 'sheets') {
      // Drawing Sheets section zoom uses a React menu (Add sheets / Select for export / expand /
      // collapse) — NOT the outline engine's section menu, which rendered a milestone menu here (bug 2).
      sheetsZoom = { msId: _secMsId, key: wf.zoomSection }
    } else if (wf.zoomSection === 'sec:design:inspiration') {
      inspoZoom = true
    } else if (_stype === 'spec-versions') {
      // Specifications section zoom — React menu with Open spec editor (the engine has no such item).
      specZoom = { msId: _secMsId, key: wf.zoomSection }
    } else {
      menuAttrs = {
        'data-fn': 'openMenu',
        'data-type': 'section',
        'data-key': wf.zoomSection,
        'data-sectiontype': _stype,
        ...(_secMsId ? { 'data-msid': _secMsId } : {}),
      }
    }
  } else if (isConceptDetail) {
    // Reuses the exact same native engine menu ConceptRow's own ⋯ button opens (data-type
    // "concept" in outline.js: Open Building Information Model / Open AlderCAD / Set as
    // active / Edit name /
    // Delete concept) — concept-detail used to be the one design-entity zoom excluded from
    // getting a menu at all; this makes it consistent with Unit/Level/Room, which already have one.
    const _cdConceptId = wf.zoomSection.slice('concept-detail:'.length)
    menuAttrs = {
      'data-fn': 'openMenu',
      'data-type': 'concept',
      'data-key': 'concept-menu:' + _cdConceptId,
      'data-conceptid': _cdConceptId,
      'data-owner': wf.zoomProjectId || ctx.recordId || '',
    }
  } else if (wf.zoomRoot && !isMeetingDetail && !isThreadDetail && !isThreadsRootZoom && !isDesignEntityZoom) {
    menuAttrs = {
      'data-fn': 'openMenu',
      'data-type': 'root',
      'data-key': 'root:' + wf.zoomRoot,
      'data-rootname': wf.zoomRoot,
    }
  }

  // Project level — menu button for project actions (expand/collapse all, etc.)
  if (!menuAttrs && wf.zoomProjectId && !wf.zoomRoot && !isDesignEntityZoom) {
    menuAttrs = {
      'data-fn': 'openMenu',
      'data-type': 'project',
      'data-key': 'proj:' + wf.zoomProjectId,
      'data-projid': wf.zoomProjectId,
    }
  }

  // Home — menu for the Projects collection (same items the old root node offered)
  if (!menuAttrs && !wf.zoomProjectId && !isDesignEntityZoom) {
    menuAttrs = {
      'data-fn': 'openMenu',
      'data-type': 'root',
      'data-key': 'root:projects',
      'data-rootname': 'projects',
    }
  }

  // Item zoom — menu button from checklist item state
  if (!menuAttrs && wf.zoomItemId && wf.zoomMsId) {
    const items = ctx.clCache ? (ctx.clCache[wf.zoomMsId] || []) : []
    const zItem = items.find(it => it.id === wf.zoomItemId)
    if (zItem) {
      const isDone = zItem.fields['Complete'] === true
      const isSkip = zItem.fields['Milestone Not Required'] === true
      menuAttrs = {
        'data-fn': 'openMenu',
        'data-type': 'item',
        'data-key': 'item:' + wf.zoomItemId,
        'data-itemid': wf.zoomItemId,
        'data-msid': wf.zoomMsId,
        'data-isdone': isDone ? 'true' : 'false',
        'data-isskip': isSkip ? 'true' : 'false',
      }
    }
  }

  // ── Inline title-bar actions (right-aligned, smaller than the title) ──────────
  let inlineActions = null
  let slackPanel = null
  const atMsZoom    = wf.zoomMsId && !wf.zoomCat && !wf.zoomItemId && !wf.zoomSection
  const atPhaseZoom = wf.zoomPhaseId && !wf.zoomMsId
  const atHome      = !wf.zoomProjectId && !wf.zoomRoot && !wf.zoomSection && !wf.zoomMsId && !wf.zoomPhaseId && !ctx.showClosedProjects

  if (atHome) {
    // Project scope (docs/FLOW_RECONCILIATION_PLAN.md, decision 10): the list defaults to the
    // projects the person is crewed on; "All" is one click away and persists per browser.
    const scope = getProjectScope()
    const pill = (key, label) => (
      <button
        type="button"
        onClick={() => setProjectScope(key)}
        className={'wf-scope-pill' + (scope === key ? ' wf-scope-pill--on' : '')}
      >{label}</button>
    )
    inlineActions = (
      <div className="wf-zoom-title-actions">
        {titleMeta && <span className="wf-zoom-title-actlbl">{titleMeta}</span>}
        <span className="wf-scope-toggle" title="Which projects the outline shows">
          {pill('mine', 'My projects')}
          {pill('all', 'All projects')}
        </span>
      </div>
    )
  } else if (atPhaseZoom) {
    const ph = (ctx.state?.phases || []).find(p => p.id === wf.zoomPhaseId)
    if (ph) {
      const allowTime = ph.fields['Allow Time Entries'] === true
      const toggle = () => {
        ph.fields['Allow Time Entries'] = !allowTime
        updatePhaseTimeEntries(ph.id, !allowTime)
        window.dispatchEvent(new Event('outline-render'))
      }
      inlineActions = (
        <div className="wf-zoom-title-actions">
          <span className="wf-zoom-title-actlbl">Time entries</span>
          <label className="wf-inline-toggle" title={allowTime ? 'Disable time entries' : 'Enable time entries'}>
            <input type="checkbox" className="wf-inline-toggle-inp" checked={allowTime} onChange={toggle} />
            <span className="wf-inline-toggle-track"><span className="wf-inline-toggle-thumb"></span></span>
          </label>
        </div>
      )
    }
  } else if (atMsZoom) {
    const ms = (ctx.state?.milestones || []).find(m => m.id === wf.zoomMsId)
    const f  = ms ? ms.fields : {}
    const threadTs  = f['Slack Thread Timestamp'] || ''
    const hasUnread = !!(threadTs && localStorage.getItem('slack:read:' + wf.zoomMsId) !== threadTs)
    inlineActions = (
      <div className="wf-zoom-title-actions">
        {/* The crew, when this milestone is in the air; nothing when it is queued (no owner by design). */}
        <CrewInline kind="milestone" id={wf.zoomMsId} style={{ marginLeft: 0 }} />
        {/* Waiting on a predecessor: read-only until it is posted, deleted, or unlinked (outline/blocked.js). */}
        {ms && !f['Posted Date'] && blockedBy(ms).length > 0 && (
          <span className="wf-blocked-tag" style={{ marginLeft: 0, overflow: 'visible' }}>
            <BlockedLock waitingOn={blockedBy(ms)} />waiting on {blockedBy(ms).map(msLabel).join(', ')}
          </span>
        )}
        {ms && !f['Posted Date'] && <StartedAheadTag msId={wf.zoomMsId} style={{ marginLeft: 0 }} />}
        {ms && f['Posted Date'] && <PostRecordTag msId={wf.zoomMsId} style={{ marginLeft: 0 }} />}
        <button
          className={`wf-slack-btn${threadTs ? ' wf-slack-btn--active' : ''}`}
          title={threadTs ? 'View discussion' : 'Start a discussion in Slack'}
          onClick={() => setShowPanel(true)}
          style={{ opacity: 1 }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          {hasUnread && <span className="wf-slack-unread-dot" />}
        </button>
      </div>
    )
    if (showPanel && ms) {
      slackPanel = (
        <SlackThreadPanel
          msId={wf.zoomMsId}
          msName={f['Full Deliverable Name'] || f['Name'] || ''}
          projectId={wf.zoomProjectId || ''}
          projectName={f['Project Name'] || ''}
          threadTs={threadTs}
          onClose={() => setShowPanel(false)}
        />
      )
    }
  } else if (isThreadDetail && threadZoom) {
    const tz = threadZoom
    const hasUnread = !!(tz.threadTs && localStorage.getItem('slack:read:' + tz.id) !== tz.threadTs)
    inlineActions = (
      <div className="wf-zoom-title-actions">
        {tz.status && <span className="wf-zoom-title-actlbl">{tz.status}</span>}
        {tz.deadline && <span className="wf-zoom-title-actlbl">{fmtShortDate(tz.deadline)}</span>}
        <CrewInline kind="thread" id={tz.id} style={{ marginLeft: 0 }} />
        <button
          className={`wf-slack-btn${tz.threadTs ? ' wf-slack-btn--active' : ''}`}
          title={tz.threadTs ? 'View discussion' : 'Start a discussion in Slack'}
          onClick={() => setShowPanel(true)}
          style={{ opacity: 1 }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          {hasUnread && <span className="wf-slack-unread-dot" />}
        </button>
      </div>
    )
    if (showPanel) {
      slackPanel = (
        <SlackThreadPanel
          entityId={tz.id}
          entityType="thread"
          entityName={tz.title}
          projectId={tz.projectId}
          projectName={tz.projectName}
          threadTs={tz.threadTs}
          onClose={() => setShowPanel(false)}
          onThreadStarted={ts => {
            const t = (ctx.threadCache || []).find(x => x.id === tz.id)
            if (t) t.fields['Slack Thread Time Stamp'] = ts
          }}
        />
      )
    }
  }

  return (
    <>
      <div className="wf-zoom-title-wrap">
        {menuAttrs && (
          <button className="wf-zoom-title-btn" {...menuAttrs}>&#8943;</button>
        )}
        {sheetsZoom && (
          <>
            {/* Title-bar styled trigger (matches the native ⋯) wired to the React menu. */}
            <button className="wf-zoom-title-btn" title="Drawing sheet actions" onClick={e => sheetsMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={sheetsMenu}>
              {sheetsZoom.msId && (
                <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-add-sheets', { detail: { msId: sheetsZoom.msId } }))}>
                  Add drawing sheets
                </ActionMenuItem>
              )}
              <ActionMenuItem onClick={() => window.dispatchEvent(new Event('enter-select-sheets'))}>Select sheets for export</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem onClick={() => { ctx.expandCollapseDescendants && ctx.expandCollapseDescendants(sheetsZoom.key, true); window.dispatchEvent(new Event('outline-render')) }}>Expand all</ActionMenuItem>
              <ActionMenuItem onClick={() => { ctx.expandCollapseDescendants && ctx.expandCollapseDescendants(sheetsZoom.key, false); window.dispatchEvent(new Event('outline-render')) }}>Collapse all</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {inspoZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Inspiration actions" onClick={e => inspoMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={inspoMenu}>
              <ActionMenuItem onClick={() => { inspoMenu.close(); openTeamInspirationBoard(wf.zoomProjectId || ctx.recordId) }}>Open inspiration board</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {specZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Specification actions" onClick={e => specMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={specMenu}>
              <ActionMenuItem onClick={() => {
                // AlderSPEC Phase 2 — own tab, deep-linked to this milestone's release.
                const pid = ctx.recordId || wf.zoomProjectId || ''
                window.open('/spec?projectId=' + encodeURIComponent(pid)
                  + '&msId=' + encodeURIComponent(specZoom.msId)
                  + (ctx.projectName ? '&projectName=' + encodeURIComponent(ctx.projectName) : ''), '_blank')
              }}>Open AlderSPEC</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {meetingZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Meeting actions" onClick={e => meetingMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={meetingMenu}>
              {meetingZoom.posted ? (
                <>
                  {meetingZoom.pdfUrl && <ActionMenuItem onClick={() => window.open(meetingZoom.pdfUrl, '_blank')}>Open issued minutes</ActionMenuItem>}
                  <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('meeting-repost', { detail: { meetingId: meetingZoom.id } }))}>Repost revised minutes</ActionMenuItem>
                  {/* Posted before its notes import was committed: finishing it reposts the minutes with the items. */}
                  {meetingZoom.draftStatus && <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-meeting-import', { detail: { meetingId: meetingZoom.id } }))}>Finish Zoom notes import…</ActionMenuItem>}
                  <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-edit-meeting', { detail: { meetingId: meetingZoom.id } }))}>Edit meeting</ActionMenuItem>
                  <ActionMenuDivider />
                  <ActionMenuItem danger onClick={() => window.dispatchEvent(new CustomEvent('meeting-delete', { detail: { meetingId: meetingZoom.id } }))}>Delete meeting</ActionMenuItem>
                </>
              ) : (
                <>
                  <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('meeting-add-thread', { detail: { meetingId: meetingZoom.id } }))}>Add thread</ActionMenuItem>
                  <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('meeting-add-open-thread', { detail: { meetingId: meetingZoom.id } }))}>Add an open thread…</ActionMenuItem>
                  {meetingZoom.draftStatus === 'ready_for_review'
                    ? <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-meeting-import', { detail: { meetingId: meetingZoom.id } }))}>Review Zoom notes</ActionMenuItem>
                    : meetingZoom.draftStatus === 'draft'
                    ? <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-meeting-import', { detail: { meetingId: meetingZoom.id } }))}>Continue Zoom notes import…</ActionMenuItem>
                    : meetingZoom.notesImported
                    ? <div className="wf-menu-item" style={{ opacity: 0.5, cursor: 'default' }} title="Zoom notes have already been imported for this meeting">Zoom notes imported ✓</div>
                    : <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-meeting-import', { detail: { meetingId: meetingZoom.id } }))}>Import Zoom notes</ActionMenuItem>}
                  <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-edit-meeting', { detail: { meetingId: meetingZoom.id } }))}>Edit meeting</ActionMenuItem>
                  <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('meeting-preview', { detail: { meetingId: meetingZoom.id } }))}>Preview PDF</ActionMenuItem>
                  <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('meeting-post', { detail: { meetingId: meetingZoom.id } }))}>Post</ActionMenuItem>
                  <ActionMenuDivider />
                  <ActionMenuItem danger onClick={() => window.dispatchEvent(new CustomEvent('meeting-delete', { detail: { meetingId: meetingZoom.id } }))}>Delete meeting</ActionMenuItem>
                </>
              )}
            </ActionMenu>
          </>
        )}
        {isThreadsRootZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Threads actions" onClick={e => threadsRootMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={threadsRootMenu}>
              <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-threads-add'))}>Add thread</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem onClick={() => { ctx.expandCollapseDescendants && ctx.expandCollapseDescendants('root:threads', true); window.dispatchEvent(new Event('outline-render')) }}>Expand all</ActionMenuItem>
              <ActionMenuItem onClick={() => { ctx.expandCollapseDescendants && ctx.expandCollapseDescendants('root:threads', false); window.dispatchEvent(new Event('outline-render')) }}>Collapse all</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {threadZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Thread actions" onClick={e => threadMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={threadMenu}>
              <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-thread-activity-modal', { detail: { threadId: threadZoom.id, threadTitle: threadZoom.title } }))}>Log Activity</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-create-thread-modal', { detail: { threadId: threadZoom.id, projectId: threadZoom.projectId } }))}>Edit thread</ActionMenuItem>
              <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-move-threads-modal', { detail: { threadId: threadZoom.id, projectId: threadZoom.projectId } }))}>Move to a milestone…</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem danger onClick={() => window.dispatchEvent(new CustomEvent('thread-delete', { detail: { threadId: threadZoom.id } }))}>Delete thread</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {unitDetailZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Unit actions" onClick={e => unitMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={unitMenu}>
              <ActionMenuItem onClick={() => { unitMenu.close(); setUnitEditOpen(true) }}>Edit type</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem danger onClick={handleDeleteUnitZoom}>Delete unit</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {levelDetailZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Level actions" onClick={e => levelMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={levelMenu}>
              <ActionMenuItem onClick={() => { levelMenu.close(); setLevelRenameOpen(true) }}>Edit name</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem danger onClick={handleDeleteLevelZoom}>Delete level</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {roomDetailZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Room actions" onClick={e => roomMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={roomMenu}>
              <ActionMenuItem danger onClick={handleDeleteRoomZoom}>Delete room</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {conceptUnitsZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Units actions" onClick={e => unitsCatMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={unitsCatMenu}>
              <ActionMenuItem onClick={() => { unitsCatMenu.close(); window.dispatchEvent(new CustomEvent('open-add-unit', { detail: { conceptId: conceptUnitsZoom.conceptId } })) }}>Add Unit</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {unitLevelsZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Levels actions" onClick={e => levelsCatMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={levelsCatMenu}>
              <ActionMenuItem onClick={() => { levelsCatMenu.close(); window.dispatchEvent(new CustomEvent('open-add-level', { detail: { unitId: unitLevelsZoom.unitId } })) }}>Add Level</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {levelRoomsZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Rooms actions" onClick={e => roomsCatMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={roomsCatMenu}>
              <ActionMenuItem onClick={() => { roomsCatMenu.close(); window.dispatchEvent(new CustomEvent('open-add-room', { detail: { levelId: levelRoomsZoom.levelId } })) }}>Add Room</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {printShopZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Print shop actions" onClick={e => printShopMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={printShopMenu}>
              <ActionMenuItem onClick={() => { printShopMenu.close(); window.dispatchEvent(new CustomEvent('open-team-print-action', { detail: { action: 'prepare', orgId: printShopZoom.orgId } })) }}>Prepare print order…</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {printOrderRec && (
          <>
            <button className="wf-zoom-title-btn" title="Print order actions" onClick={e => printOrderMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={printOrderMenu}>
              {PRINT_STATUSES.filter(st => st.v !== 'failed' && st.v !== printOrderRec.status).map(st => (
                <ActionMenuItem key={st.v} onClick={() => { printOrderMenu.close(); window.dispatchEvent(new CustomEvent('open-team-print-action', { detail: { action: 'status', status: st.v, orderId: printOrderRec.id } })) }}>Mark {st.l.toLowerCase()}</ActionMenuItem>
              ))}
              <ActionMenuDivider />
              <ActionMenuItem onClick={() => { printOrderMenu.close(); window.dispatchEvent(new CustomEvent('open-team-print-action', { detail: { action: 'again', orderId: printOrderRec.id } })) }}>Order again…</ActionMenuItem>
              <ActionMenuItem danger onClick={() => { printOrderMenu.close(); window.dispatchEvent(new CustomEvent('open-team-print-action', { detail: { action: 'delete', orderId: printOrderRec.id } })) }}>Delete this record</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {candidateDetailZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Candidate actions" onClick={e => candidateMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={candidateMenu}>
              <ActionMenuItem onClick={() => { candidateMenu.close(); window.dispatchEvent(new CustomEvent('open-team-candidate-action', { detail: { action: 'request', projectConsultantId: candidateDetailZoom.id } })) }}>Request proposal</ActionMenuItem>
              <ActionMenuItem onClick={() => { candidateMenu.close(); window.dispatchEvent(new CustomEvent('open-team-candidate-action', { detail: { action: 'log', projectConsultantId: candidateDetailZoom.id } })) }}>Log proposal received</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem onClick={() => { candidateMenu.close(); window.dispatchEvent(new CustomEvent('open-team-candidate-action', { detail: { action: 'award', projectConsultantId: candidateDetailZoom.id } })) }}>Award — move onto the team</ActionMenuItem>
              <ActionMenuItem danger onClick={() => { candidateMenu.close(); window.dispatchEvent(new CustomEvent('open-team-candidate-action', { detail: { action: 'remove', projectConsultantId: candidateDetailZoom.id } })) }}>Remove candidate</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {rfpDetailZoom && (
          <>
            <button className="wf-zoom-title-btn" title="RFP actions" onClick={e => rfpMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={rfpMenu}>
              <ActionMenuItem onClick={() => { rfpMenu.close(); window.dispatchEvent(new CustomEvent('open-team-rfp-action', { detail: { action: 'log', rfpId: rfpDetailZoom.id } })) }}>Log proposal for this round</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem danger onClick={() => { rfpMenu.close(); window.dispatchEvent(new CustomEvent('open-team-rfp-action', { detail: { action: 'delete', rfpId: rfpDetailZoom.id } })) }}>Delete RFP</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {proposalDetailZoom && (
          <>
            <button className="wf-zoom-title-btn" title="Proposal actions" onClick={e => proposalMenu.toggle(e.currentTarget)}>&#8943;</button>
            <ActionMenu menu={proposalMenu}>
              <ActionMenuItem onClick={() => { proposalMenu.close(); window.dispatchEvent(new CustomEvent('open-team-proposal-action', { detail: { action: 'edit', proposalId: proposalDetailZoom.id } })) }}>Edit proposal</ActionMenuItem>
              <ActionMenuItem onClick={() => { proposalMenu.close(); window.dispatchEvent(new CustomEvent('open-team-proposal-action', { detail: { action: 'decide', proposalId: proposalDetailZoom.id } })) }}>Record client decision</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem danger onClick={() => { proposalMenu.close(); window.dispatchEvent(new CustomEvent('open-team-proposal-action', { detail: { action: 'delete', proposalId: proposalDetailZoom.id } })) }}>Delete proposal</ActionMenuItem>
            </ActionMenu>
          </>
        )}
        {unitEditOpen && unitDetailZoom && (
          <UnitTypeModal
            title="Edit Unit Type"
            initial={unitDetailZoom.unitType}
            confirmLabel="Save"
            onSave={handleRenameUnitZoom}
            onClose={() => setUnitEditOpen(false)}
          />
        )}
        {levelRenameOpen && levelDetailZoom && (
          <LevelRenameModal
            initial={levelDetailZoom.name}
            onSave={handleRenameLevelZoom}
            onClose={() => setLevelRenameOpen(false)}
          />
        )}
        {/* key forces element recreation on title change, re-triggering the entrance animation */}
        <div className="wf-zoom-title" key={title}>{title}</div>
        {/* Copy a shareable deep link to this exact view (for Slack etc.). */}
        {title && (
          <button className="wf-zoom-title-btn" title={copied ? 'Link copied' : 'Copy link to this view'}
            onClick={copyLink} style={{ color: copied ? '#1a7a4a' : undefined }}>
            {copied ? (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3.5 8.5l3 3 6-6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6.5 9.5a2.5 2.5 0 003.5 0l2-2a2.5 2.5 0 00-3.5-3.5l-1 1M9.5 6.5a2.5 2.5 0 00-3.5 0l-2 2a2.5 2.5 0 003.5 3.5l1-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            )}
          </button>
        )}
        {titleMeta && !inlineActions && (
          <span className="wf-zoom-title-actions"><span className="wf-zoom-title-actlbl">{titleMeta}</span></span>
        )}
        {inlineActions}
      </div>
      {/* The project's Build Ready countdown, on the project's own page only (docs/ALDERFLOW.md §5c2). */}
      {wf.zoomProjectId && !wf.zoomRoot && !wf.zoomSection && !wf.zoomMsId && !wf.zoomPhaseId && !wf.zoomItemId && !wf.zoomCat && !wf.zoomConceptId && <BuildReadyStrip projectId={wf.zoomProjectId} />}
      {slackPanel}
    </>
  )
}
